/**
 * Frost overlay effect for a glass surface.
 *
 * Generates fixed geometry representing frost growth and reveals it as the cabin gets colder.
 * Geometry includes feathery ice ferns, milky hazes, and tiny grains of rime.
 * Growth is gradual, starting at edges and corners, and recedes as the cabin warms.
 */

// ---------------------------------------------------------------------------

const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

// ---------------------------------------------------------------------------

type Segment = { x1: number; y1: number; x2: number; y2: number; birth: number; width: number };
type Haze = { x: number; y: number; radius: number; birth: number; strength: number };
type Grain = { x: number; y: number; radius: number; birth: number; alpha: number };

type FrostGeometry = {
  width: number;
  height: number;
  segments: Segment[];
  hazes: Haze[];
  grains: Grain[];
};

const FERN_GROWTH_SPAN = 0.4;
const SEED = 0x4a2f1b;

const growFern = (
  segments: Segment[],
  rng: () => number,
  x: number,
  y: number,
  angle: number,
  length: number,
  width: number,
  birthStart: number,
  order: number
): void => {
  const pieces = Math.max(3, Math.round(length / 9));
  const step = length / pieces;
  let cx = x;
  let cy = y;
  let a = angle;
  for (let i = 0; i < pieces; i += 1) {
    a += (rng() - 0.5) * 0.18;
    const nx = cx + Math.cos(a) * step;
    const ny = cy + Math.sin(a) * step;
    const along = (i + 1) / pieces;
    const birth = birthStart + along * FERN_GROWTH_SPAN * (order === 0 ? 1 : 0.7);
    const taper = 1 - along * 0.55;
    segments.push({ x1: cx, y1: cy, x2: nx, y2: ny, birth, width: Math.max(0.45, width * taper) });

    if (order < 2 && i > 0 && i % 2 === 0 && rng() > 0.12) {
      const side = (i / 2) % 2 === 0 ? 1 : -1;
      const branchAngle = a + side * (Math.PI / 3 + (rng() - 0.5) * 0.35);
      const branchLength = length * (0.34 - along * 0.2) * (0.7 + rng() * 0.5);
      if (branchLength > 4) {
        growFern(segments, rng, nx, ny, branchAngle, branchLength, width * 0.62, birth, order + 1);
      }
    }
    cx = nx;
    cy = ny;
  }
};

const buildGeometry = (width: number, height: number): FrostGeometry => {
  const rng = mulberry32(SEED ^ (width * 31 + height));
  const reach = Math.max(width, height);
  const segments: Segment[] = [];
  const hazes: Haze[] = [];
  const grains: Grain[] = [];

  const siteCount = Math.round(70 + reach / 14);
  for (let i = 0; i < siteCount; i += 1) {
    const edge = Math.floor(rng() * 4);
    let u = rng();
    u = u < 0.5 ? Math.pow(u * 2, 1.7) / 2 : 1 - Math.pow((1 - u) * 2, 1.7) / 2;
    let x = 0;
    let y = 0;
    let inward = 0;
    if (edge === 0) { x = u * width; y = 0; inward = Math.PI / 2; }
    else if (edge === 1) { x = width; y = u * height; inward = Math.PI; }
    else if (edge === 2) { x = u * width; y = height; inward = -Math.PI / 2; }
    else { x = 0; y = u * height; inward = 0; }

    const cornerness = 1 - Math.min(1, Math.min(u, 1 - u) * 4);
    const birth = (0.04 + rng() * 0.5) * (1 - cornerness * 0.5);
    const length = reach * (0.05 + rng() * 0.11) * (0.75 + cornerness * 0.6);
    const fan = 1 + Math.floor(rng() * 2);
    for (let f = 0; f < fan; f += 1) {
      growFern(segments, rng, x, y, inward + (rng() - 0.5) * 1.0 + (f - (fan - 1) / 2) * 0.5, length, 1.5 + rng() * 1.1, birth, 0);
    }

    hazes.push({
      x: x + Math.cos(inward) * length * 0.35,
      y: y + Math.sin(inward) * length * 0.35,
      radius: length * (0.7 + rng() * 0.5),
      birth: birth * 0.9,
      strength: 0.07 + rng() * 0.08
    });
  }

  const patchCount = 6;
  for (let i = 0; i < patchCount; i += 1) {
    const x = width * (0.15 + rng() * 0.7);
    const y = height * (0.15 + rng() * 0.7);
    const birth = 0.45 + rng() * 0.35;
    hazes.push({ x, y, radius: reach * (0.06 + rng() * 0.08), birth, strength: 0.08 + rng() * 0.06 });
    growFern(segments, rng, x, y, rng() * Math.PI * 2, reach * (0.03 + rng() * 0.05), 1.2, birth, 1);
  }

  const grainCount = Math.round((width * height) / 700);
  for (let i = 0; i < grainCount; i += 1) {
    const segment = segments[Math.floor(rng() * segments.length)];
    if (!segment) break;
    const spread = 7 + rng() * 14;
    grains.push({
      x: segment.x2 + (rng() - 0.5) * spread * 2,
      y: segment.y2 + (rng() - 0.5) * spread * 2,
      radius: 0.5 + rng() * 1.3,
      birth: Math.min(0.98, segment.birth + rng() * 0.12),
      alpha: 0.35 + rng() * 0.5
    });
  }

  return { width, height, segments, hazes, grains };
};

// ---------------------------------------------------------------------------

const FRESH_SPAN = 0.05;

const renderFrost = (ctx: CanvasRenderingContext2D, geometry: FrostGeometry, progress: number): void => {
  const { width, height } = geometry;
  ctx.clearRect(0, 0, width, height);
  if (progress <= 0.004) return;

  for (const haze of geometry.hazes) {
    const age = progress - haze.birth;
    if (age <= 0) continue;
    const grown = smoothstep(0, 0.35, age);
    const radius = haze.radius * (0.25 + 0.75 * grown);
    const gradient = ctx.createRadialGradient(haze.x, haze.y, 0, haze.x, haze.y, radius);
    const alpha = haze.strength * grown * (0.6 + 0.4 * progress);
    gradient.addColorStop(0, `rgba(228, 242, 255, ${alpha.toFixed(3)})`);
    gradient.addColorStop(0.6, `rgba(205, 228, 250, ${(alpha * 0.45).toFixed(3)})`);
    gradient.addColorStop(1, 'rgba(205, 228, 250, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(haze.x, haze.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  const veil = smoothstep(0.25, 1, progress) * 0.16;
  if (veil > 0.003) {
    const depth = Math.max(width, height) * (0.08 + 0.12 * progress);
    const edges: Array<[number, number, number, number]> = [
      [0, 0, 0, depth], [0, height, 0, height - depth], [0, 0, depth, 0], [width, 0, width - depth, 0]
    ];
    for (const [x0, y0, x1, y1] of edges) {
      const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
      gradient.addColorStop(0, `rgba(222, 238, 255, ${veil.toFixed(3)})`);
      gradient.addColorStop(1, 'rgba(222, 238, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }
  }

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const settled: Segment[] = [];
  const fresh: Segment[] = [];
  for (const segment of geometry.segments) {
    if (segment.birth > progress) continue;
    (progress - segment.birth < FRESH_SPAN ? fresh : settled).push(segment);
  }
  const strokeAll = (list: Segment[], lineScale: number, style: string): void => {
    if (list.length === 0) return;
    ctx.strokeStyle = style;
    const bands = new Map<number, Segment[]>();
    for (const segment of list) {
      const key = Math.round(segment.width * 2) / 2;
      let band = bands.get(key);
      if (!band) bands.set(key, band = []);
      band.push(segment);
    }
    for (const [bandWidth, members] of bands) {
      ctx.lineWidth = bandWidth * lineScale;
      ctx.beginPath();
      for (const segment of members) {
        ctx.moveTo(segment.x1, segment.y1);
        ctx.lineTo(segment.x2, segment.y2);
      }
      ctx.stroke();
    }
  };
  strokeAll(settled, 3.4, 'rgba(160, 208, 255, 0.10)');
  strokeAll(fresh, 3.4, 'rgba(160, 208, 255, 0.05)');
  strokeAll(settled, 1.25, 'rgba(226, 243, 255, 0.62)');
  strokeAll(fresh, 1.25, 'rgba(226, 243, 255, 0.34)');
  strokeAll(settled, 0.45, 'rgba(255, 255, 255, 0.85)');

  ctx.fillStyle = 'rgb(240, 249, 255)';
  for (const grain of geometry.grains) {
    if (grain.birth > progress) continue;
    ctx.globalAlpha = grain.alpha * smoothstep(0, 0.06, progress - grain.birth);
    ctx.beginPath();
    ctx.arc(grain.x, grain.y, grain.radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
};

// ---------------------------------------------------------------------------

const PROGRESS_STEPS = 120;
const MAX_PIXEL_RATIO = 2;

export type FrostLayer = {
  update: (progress: number) => void;
};

export const createFrostLayer = (host: HTMLElement): FrostLayer => {
  const canvas = document.createElement('canvas');
  canvas.className = 'frost-canvas';
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
  host.append(canvas);

  let geometry: FrostGeometry | null = null;
  let drawnKey = '';

  return {
    update: (progress: number): void => {
      const quantized = Math.round(clamp01(progress) * PROGRESS_STEPS) / PROGRESS_STEPS;
      const cssWidth = canvas.clientWidth;
      const cssHeight = canvas.clientHeight;
      if (cssWidth === 0 || cssHeight === 0) return;
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const pixelWidth = Math.round(cssWidth * ratio);
      const pixelHeight = Math.round(cssHeight * ratio);
      const key = `${pixelWidth}x${pixelHeight}:${quantized}`;
      if (key === drawnKey) return;
      drawnKey = key;

      if (!geometry || geometry.width !== pixelWidth || geometry.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
        geometry = buildGeometry(pixelWidth, pixelHeight);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      renderFrost(ctx, geometry, quantized);
    }
  };
};
